'use client';

import { memo, useRef, useState } from 'react';
import { FeedPost, formatCount, initials } from './types';
import { HoverLift, MotionButton } from '../motion';

interface MediaTileProps {
  post: FeedPost;
  currentUserId?: string;
  isFollowing: boolean;
  followPending: boolean;
  onLike: (id: string) => void;
  onOpenPost: (id: string) => void;
  onNavigate: (path: string) => void;
  onFollow: (userId: string) => void;
  onShare: (post: FeedPost) => void;
}

// A media-first discovery tile for the masonry grid. Image/video fills the
// card; metadata + actions live in a hover/focus scrim so the grid stays clean.
const MediaTile = memo(function MediaTile({
  post,
  currentUserId,
  isFollowing,
  followPending,
  onLike,
  onOpenPost,
  onNavigate,
  onFollow,
  onShare,
}: MediaTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [imgError, setImgError] = useState(false);
  const isVideo = post.contentType === 'video';
  const isOwner = currentUserId === post.userId;
  // Prefer the lightweight thumbnail; fall back to full media.
  const poster = post.thumbnailUrl || post.mediaUrl;

  const handleEnter = () => {
    if (isVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };
  const handleLeave = () => {
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const open = () => onOpenPost(post.id);
  const onKeyOpen = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <HoverLift
      lift={-2}
      scale={1.01}
      className="group relative mb-4 break-inside-avoid rounded-2xl overflow-hidden bg-white/[0.04] cursor-pointer transition-colors"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={open}
      onKeyDown={onKeyOpen}
      role="button"
      tabIndex={0}
      aria-label={`Open post by ${post.displayName}`}
    >
      {/* ── Media ── */}
      {isVideo ? (
        <video
          ref={videoRef}
          src={post.mediaUrl}
          poster={post.thumbnailUrl || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full block"
        />
      ) : imgError ? (
        <div className="w-full aspect-square flex items-center justify-center bg-white/[0.05] text-white/20 text-xs">
          Unavailable
        </div>
      ) : (
        <img
          src={poster}
          alt={post.caption || `Creation by ${post.displayName}`}
          loading="lazy"
          onError={() => setImgError(true)}
          className="w-full block"
        />
      )}

      {/* Video play glyph (always visible, dimmed on hover when playing) */}
      {isVideo && (
        <div className="pointer-events-none absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center transition-opacity group-hover:opacity-0">
          <svg className="w-3.5 h-3.5 text-white translate-x-[1px]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}

      {/* Featured ribbon */}
      {post.isFeatured && (
        <div className="pointer-events-none absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/90 backdrop-blur-sm text-white text-[10px] font-semibold shadow-lg">
          <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z" />
          </svg>
          Featured
        </div>
      )}

      {/* ── Hover / focus scrim ── */}
      <div className="absolute inset-0 flex flex-col justify-between opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200">
        {/* top gradient holds quick actions */}
        <div className="h-24 bg-gradient-to-b from-black/70 to-transparent flex items-start justify-end p-2 gap-1.5">
          <MotionButton
            onClick={(e) => { stop(e); onLike(post.id); }}
            aria-label={post.liked ? 'Unlike' : 'Like'}
            className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <svg
              className={`w-4 h-4 ${post.liked ? 'text-red-500 fill-red-500' : 'text-white'}`}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={post.liked ? 0 : 2}
              fill={post.liked ? 'currentColor' : 'none'}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </MotionButton>
          <MotionButton
            onClick={(e) => { stop(e); open(); }}
            aria-label="View comments"
            className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </MotionButton>
          <MotionButton
            onClick={(e) => { stop(e); onShare(post); }}
            aria-label="Share"
            className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </MotionButton>
        </div>

        {/* bottom gradient holds creator + counts + follow */}
        <div className="bg-gradient-to-t from-black/85 via-black/40 to-transparent px-2.5 pt-8 pb-2.5">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0 flex-1">
              <button
                onClick={(e) => { stop(e); onNavigate(`/dashboard/community/profile/${post.userId}`); }}
                className="flex items-center gap-2 min-w-0 max-w-full hover:opacity-80 transition-opacity"
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 p-[1.5px] flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-[9px] text-white/80 font-bold overflow-hidden">
                    {post.avatarUrl
                      ? <img src={post.avatarUrl} alt="" className="w-full h-full object-cover" />
                      : initials(post.displayName)}
                  </div>
                </div>
                <span className="text-white text-xs font-semibold truncate drop-shadow">{post.displayName}</span>
              </button>
              <div className="flex items-center gap-3 mt-1.5 text-white/80 text-[11px]">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                  {formatCount(post.likesCount)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {formatCount(post.commentsCount)}
                </span>
              </div>
            </div>

            {/* Inline follow — only for other users when signed in */}
            {!isOwner && currentUserId && (
              <MotionButton
                onClick={(e) => { stop(e); onFollow(post.userId); }}
                disabled={followPending}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors disabled:opacity-60 ${
                  isFollowing
                    ? 'bg-white/15 text-white/80 hover:bg-white/25'
                    : 'bg-white text-black hover:bg-white/90'
                }`}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </MotionButton>
            )}
          </div>
        </div>
      </div>
    </HoverLift>
  );
});

export default MediaTile;
